import { Component, Inject, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AudienceService } from 'app/library/audience/services/audience.service';
import { ScheduledStreamContentService } from 'app/library/scheduled-stream-content/services/scheduled-stream-content.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IMaxBrainEnvironment } from 'app/projects/shared/src/lib/interfaces/environment.interface';
import { takeUntil } from 'rxjs/operators';
import { ContentType, ContentTypeItem } from '../../models/content-types.model';
import { MatStepper } from '@angular/material/stepper';
import { ContentTypeEnum } from '../../models/content-type.enum';
import { AudienceRelevantExperience } from 'app/library/grow/models/audience-relevant-experience.model';
import { EngageEditFeaturedContentDialogComponent } from '../engage-edit-featured-content-dialog/engage-edit-featured-content-dialog.component';
import { LiveCategoryLink } from 'app/library/live-category-link/models/live-category-link.model';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Article } from 'app/library/article/models/article.model';
import { UnsetAllPostings } from 'app/library/posting/actions/all-postings.action';
import { Posting } from 'app/library/posting/models/posting.model';
import { Store } from '@ngxs/store';
@Component({
    selector: 'app-engage-add-featured-content-dialog',
    templateUrl: './engage-add-featured-content-dialog.component.html',
    styleUrls: ['./engage-add-featured-content-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class EngageAddFeaturedContentDialogComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy {
    @ViewChild(MatStepper) stepper: MatStepper;

    @Input()
    audienceRelevantExperiences: AudienceRelevantExperience[] = [];

    @Input()
    liveCategoryLinks: LiveCategoryLink[] = [];

    @Input()
    engageEditFeaturedContentDialogComponent: MatDialogRef<EngageEditFeaturedContentDialogComponent>;

    @Input()
    widgetId: string;

    @Input()
    audienceId: string;

    @Input()
    featuredStreamContentId: string;

    @Input()
    hideContentTypeStep = false;

    @Input()
    initialSelectedContentTypeId: ContentTypeEnum | null = null;

    @Input()
    articleId: string;

    @Input()
    existingRelatedPostings: Posting[] = [];

    contentTypes = ContentType.all;
    contentTypeEnum = ContentTypeEnum;
    selectedContentTypeId: number = null;
    isCourseCatalogFeatureEnabled: boolean;

    get shouldShowStepper(): boolean {
        return !this.hideContentTypeStep;
    }

    get hasGrowWritePermission(): boolean {
        const permissions = this._store.selectSnapshot(UserState.getMyInfo)?.permissions ?? [];

        return permissions.includes(AdminPermissions.GROW_WRITE) || permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
    }

    get canUseCatalogContent(): boolean {
        return this.isCourseCatalogFeatureEnabled && this.hasGrowWritePermission;
    }

    get visibleContentTypes(): ContentTypeItem[] {
        if (this.canUseCatalogContent) {
            return this.contentTypes;
        }

        return this.contentTypes.filter((contentType) => !this.isCatalogBasedContentType(contentType.value));
    }

    get isLastStep(): boolean {
        return this.stepper?.steps && this?.stepper.selectedIndex === this?.stepper.steps.length - 1;
    }

    constructor(
        @Inject(MAXBRAIN_ENVIRONMENT) private _environment: IMaxBrainEnvironment,
        public dialogRef: MatDialogRef<EngageAddFeaturedContentDialogComponent>,
        private _featuredStreamContentService: ScheduledStreamContentService,
        public audienceService: AudienceService,
        private _featureSwitchPipe: FeatureSwitchPipe,
        private _store: Store
    ) {
        super();
        this.isCourseCatalogFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.CourseCatalog);
    }

    async ngOnInit(): Promise<void> {
        if (this.initialSelectedContentTypeId !== null && this.initialSelectedContentTypeId !== undefined) {
            this.selectedContentTypeId = this.initialSelectedContentTypeId;
        }

        this._featuredStreamContentService.newScheduledStreamContentSubject$.pipe(takeUntil(this._unsubscribeAll)).subscribe(async () => {
            await this.audienceService.fetchAudiences();
            this.audienceService.getAudiences();
        });
    }

    onSelectContentType(contentType: ContentTypeItem): void {
        this.selectedContentTypeId = contentType.value;

        setTimeout(() => {
            this.stepper.next();
        });
    }

    closeDialog(closeEvent?: boolean | { closeParentDialog?: boolean; result?: Article }): void {
        const closeParentDialog = typeof closeEvent === 'boolean' ? closeEvent : closeEvent?.closeParentDialog;
        const result = typeof closeEvent === 'object' ? closeEvent.result : null;

        this.dialogRef.close(result);

        if (closeParentDialog) {
            this.engageEditFeaturedContentDialogComponent?.close();
        }
    }

    isCatalogBasedContentType(contentTypeId: number): boolean {
        return [this.contentTypeEnum.Course, this.contentTypeEnum.Category, this.contentTypeEnum.Live_Link].includes(contentTypeId);
    }

    ngOnDestroy(): void {
        this._store.dispatch(new UnsetAllPostings());
        super.ngOnDestroy();
    }
}
